<template>
  <div class="ob-page">
    <div class="ob-modal">
      <!-- Panneau visuel gauche -->
      <div class="ob-left">
        <div class="ob-left-overlay"></div>
        <div class="ob-left-content">
          <img :src="logoWhite" alt="Peelo" class="ob-logo" />
          <div class="ob-left-text">
            <h2>Bienvenue chez Peelo</h2>
            <p>Créez, entraînez et déployez votre agent IA — sur WhatsApp et votre site web.</p>
          </div>
          <ul class="ob-left-points">
            <li><span class="dot"></span> Construisez votre agent en quelques minutes</li>
            <li><span class="dot"></span> Connectez WhatsApp &amp; votre site</li>
            <li><span class="dot"></span> Suivez vos utilisateurs et conversations</li>
          </ul>
        </div>
      </div>

      <!-- Formulaire droite -->
      <div class="ob-right">
        <div class="ob-right-head">
          <span class="ob-eyebrow">Finalisons votre profil</span>
          <h1>Ravi de vous voir{{ firstName ? ', ' + firstName : '' }} !</h1>
          <p class="ob-sub">Quelques infos pour mieux vous connaître.</p>
        </div>

        <form @submit.prevent="completeRegistration" class="ob-form">
          <!-- Entreprise -->
          <div class="ob-field">
            <label>Nom de l'entreprise</label>
            <input
              type="text"
              v-model="formData.company"
              placeholder="Ex : Ma Boutique"
              :class="{ 'input-error': fieldErrors.company }"
            />
            <div v-if="fieldErrors.company" class="error-message">{{ fieldErrors.company }}</div>
          </div>

          <!-- Pays (recherche + drapeaux) -->
          <div class="ob-field" ref="countryRef">
            <label>Pays</label>
            <div class="ob-combo" :class="{ open: countryOpen, 'input-error': fieldErrors.country }">
              <button type="button" class="ob-combo-trigger" @click="toggleCountry">
                <template v-if="selectedCountry">
                  <img class="flag" :src="flagUrl(selectedCountry.code)" :alt="selectedCountry.code" />
                  <span class="ob-combo-value">{{ selectedCountry.name }}</span>
                </template>
                <span v-else class="ob-combo-ph">Sélectionnez votre pays</span>
                <svg class="ob-combo-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </button>

              <div v-if="countryOpen" class="ob-combo-panel" @click.stop>
                <div class="ob-combo-searchbox">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input ref="countrySearchInput" v-model="countrySearch" type="text" placeholder="Rechercher un pays…" />
                </div>
                <ul class="ob-combo-list">
                  <li
                    v-for="c in filteredCountries"
                    :key="c.code"
                    :class="{ active: formData.country === c.code }"
                    @click="selectCountry(c)"
                  >
                    <img class="flag" :src="flagUrl(c.code)" :alt="c.code" />
                    <span class="ob-combo-name">{{ c.name }}</span>
                    <span class="ob-combo-dial">{{ c.prefix }}</span>
                  </li>
                  <li v-if="filteredCountries.length === 0" class="ob-combo-empty">Aucun pays trouvé</li>
                </ul>
              </div>
            </div>
            <div v-if="fieldErrors.country" class="error-message">{{ fieldErrors.country }}</div>
          </div>

          <!-- Téléphone : indicatif INDÉPENDANT du pays (cherchable + drapeaux) -->
          <div class="ob-field" ref="dialRef">
            <label>Téléphone</label>
            <div class="ob-phone" :class="{ 'input-error': fieldErrors.phoneNumber, open: dialOpen }">
              <button type="button" class="ob-phone-cc" @click="toggleDial">
                <img v-if="selectedDial" class="flag" :src="flagUrl(selectedDial.code)" :alt="selectedDial.code" />
                <span class="dial">{{ phonePrefix }}</span>
                <svg class="ob-dial-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <input type="tel" v-model="formData.phoneNumber" placeholder="77 123 45 67" />

              <div v-if="dialOpen" class="ob-combo-panel ob-dial-panel" @click.stop>
                <div class="ob-combo-searchbox">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input ref="dialSearchInput" v-model="dialSearch" type="text" placeholder="Rechercher un indicatif…" />
                </div>
                <ul class="ob-combo-list">
                  <li
                    v-for="c in filteredDials"
                    :key="c.code"
                    :class="{ active: dialCode === c.code }"
                    @click="selectDial(c)"
                  >
                    <img class="flag" :src="flagUrl(c.code)" :alt="c.code" />
                    <span class="ob-combo-name">{{ c.name }}</span>
                    <span class="ob-combo-dial">{{ c.prefix }}</span>
                  </li>
                  <li v-if="filteredDials.length === 0" class="ob-combo-empty">Aucun indicatif trouvé</li>
                </ul>
              </div>
            </div>
            <div v-if="fieldErrors.phoneNumber" class="error-message">{{ fieldErrors.phoneNumber }}</div>
          </div>

          <!-- Objectif -->
          <div class="ob-field">
            <label>Quel est votre objectif ?</label>
            <div class="ob-select">
              <select v-model="formData.userType">
                <option value="" disabled>Sélectionnez un objectif</option>
                <option value="guide">Guider &amp; orienter mes clients</option>
                <option value="support">Support client &amp; ventes</option>
                <option value="peeloshop">Boutique en ligne / E-commerce</option>
                <option value="marketing">Marketing &amp; campagnes</option>
                <option value="developer">Développeur / API</option>
              </select>
              <svg class="ob-select-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>

          <div v-if="fieldErrors.general" class="general-error-message">{{ fieldErrors.general }}</div>

          <button type="submit" class="ob-submit" :disabled="isLoading">
            <span v-if="!isLoading">Continuer</span>
            <span v-else>Chargement…</span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { apiBaseUrl } from '../config';
import logoWhite from '@/assets/logo-white.png';

export default {
  name: 'Onboarding',
  setup() {
    const router = useRouter();
    const isLoading = ref(false);
    const firstName = ref('');

    const formData = ref({ company: '', country: 'SN', phoneNumber: '', userType: '' });
    const fieldErrors = ref({ company: '', country: '', phoneNumber: '', general: '' });
    const dialCode = ref('SN');

    const countries = ref([
      { code: 'SN', name: 'Sénégal', prefix: '+221' },
      { code: 'CI', name: 'Côte d\'Ivoire', prefix: '+225' },
      { code: 'ML', name: 'Mali', prefix: '+223' },
      { code: 'BF', name: 'Burkina Faso', prefix: '+226' },
      { code: 'NE', name: 'Niger', prefix: '+227' },
      { code: 'TG', name: 'Togo', prefix: '+228' },
      { code: 'BJ', name: 'Bénin', prefix: '+229' },
      { code: 'GN', name: 'Guinée', prefix: '+224' },
      { code: 'GW', name: 'Guinée-Bissau', prefix: '+245' },
      { code: 'MR', name: 'Mauritanie', prefix: '+222' },
      { code: 'GH', name: 'Ghana', prefix: '+233' },
      { code: 'NG', name: 'Nigeria', prefix: '+234' },
      { code: 'CM', name: 'Cameroun', prefix: '+237' },
      { code: 'GA', name: 'Gabon', prefix: '+241' },
      { code: 'CG', name: 'Congo', prefix: '+242' },
      { code: 'CD', name: 'RD Congo', prefix: '+243' },
      { code: 'TD', name: 'Tchad', prefix: '+235' },
      { code: 'MA', name: 'Maroc', prefix: '+212' },
      { code: 'DZ', name: 'Algérie', prefix: '+213' },
      { code: 'TN', name: 'Tunisie', prefix: '+216' },
      { code: 'FR', name: 'France', prefix: '+33' },
      { code: 'BE', name: 'Belgique', prefix: '+32' },
      { code: 'CH', name: 'Suisse', prefix: '+41' },
      { code: 'PT', name: 'Portugal', prefix: '+351' },
      { code: 'ES', name: 'Espagne', prefix: '+34' },
      { code: 'IT', name: 'Italie', prefix: '+39' },
      { code: 'DE', name: 'Allemagne', prefix: '+49' },
      { code: 'GB', name: 'Royaume-Uni', prefix: '+44' },
      { code: 'CA', name: 'Canada', prefix: '+1' },
      { code: 'US', name: 'États-Unis', prefix: '+1' }
    ]);

    const countryOpen = ref(false);
    const countrySearch = ref('');
    const countryRef = ref(null);
    const countrySearchInput = ref(null);

    function filterList(query) {
      const q = (query || '').trim().toLowerCase();
      if (!q) return countries.value;
      return countries.value.filter(c =>
        c.name.toLowerCase().includes(q) || c.prefix.includes(q) || c.code.toLowerCase().includes(q)
      );
    }

    const selectedCountry = computed(() => countries.value.find(c => c.code === formData.value.country) || null);
    const filteredCountries = computed(() => filterList(countrySearch.value));

    // Indicatif téléphonique INDÉPENDANT du pays (ex : au Sénégal avec un indicatif français)
    const dialOpen = ref(false);
    const dialSearch = ref('');
    const dialRef = ref(null);
    const dialSearchInput = ref(null);
    const selectedDial = computed(() => countries.value.find(c => c.code === dialCode.value) || null);
    const phonePrefix = computed(() => selectedDial.value ? selectedDial.value.prefix : '');
    const filteredDials = computed(() => filterList(dialSearch.value));

    const flagUrl = (code) => `https://flagcdn.com/h20/${String(code).toLowerCase()}.png`;

    const toggleCountry = async () => {
      countryOpen.value = !countryOpen.value;
      if (countryOpen.value) {
        dialOpen.value = false;
        countrySearch.value = '';
        await nextTick();
        if (countrySearchInput.value) countrySearchInput.value.focus();
      }
    };
    const selectCountry = (c) => {
      formData.value.country = c.code;
      dialCode.value = c.code; // l'indicatif suit le pays par défaut, mais reste modifiable ensuite
      countryOpen.value = false;
    };

    const toggleDial = async () => {
      dialOpen.value = !dialOpen.value;
      if (dialOpen.value) {
        countryOpen.value = false;
        dialSearch.value = '';
        await nextTick();
        if (dialSearchInput.value) dialSearchInput.value.focus();
      }
    };
    const selectDial = (c) => {
      dialCode.value = c.code;
      dialOpen.value = false;
    };

    const onClickOutside = (e) => {
      if (countryOpen.value && countryRef.value && !countryRef.value.contains(e.target)) countryOpen.value = false;
      if (dialOpen.value && dialRef.value && !dialRef.value.contains(e.target)) dialOpen.value = false;
    };

    const completeRegistration = async () => {
      try {
        isLoading.value = true;
        fieldErrors.value = { company: '', country: '', phoneNumber: '', general: '' };

        const userData = localStorage.getItem('user');
        if (!userData) {
          fieldErrors.value.general = 'Session expirée. Veuillez vous reconnecter.';
          setTimeout(() => router.push('/login'), 2000);
          return;
        }
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const phone = `${phonePrefix.value}${formData.value.phoneNumber}`;

        const response = await axios.post(
          `${apiBaseUrl}/secure/complete-registration`,
          {
            company: formData.value.company,
            country: formData.value.country,
            phone: phone,
            userType: formData.value.userType
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data && response.data.token) {
          const updatedUserData = response.data.data;
          updatedUserData.token = response.data.token;
          localStorage.setItem('user', JSON.stringify({ data: updatedUserData }));
          // Onboarding Peelo Chat -> on reste dans le dashboard Peelo Chat (même domaine,
          // ex. sandbox.peelo.io en test). L'e-commerce reste accessible via la carte « Peelo-shop ».
          await router.push('/dashboard');
        }
      } catch (error) {
        console.error('Erreur lors de la complétion de l\'inscription:', error);
        fieldErrors.value.general = 'Une erreur est survenue. Veuillez réessayer.';
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      const userData = localStorage.getItem('user');
      if (!userData) { router.push('/login'); return; }
      try {
        const u = JSON.parse(userData);
        const name = (u.data && (u.data.firstname || u.data.name)) || '';
        firstName.value = String(name).trim().split(' ')[0] || '';
      } catch (_) { /* ignore */ }
      document.addEventListener('mousedown', onClickOutside);
    });
    onUnmounted(() => document.removeEventListener('mousedown', onClickOutside));

    return {
      logoWhite, firstName, formData, fieldErrors, phonePrefix, countries, dialCode,
      countryOpen, countrySearch, countryRef, countrySearchInput,
      selectedCountry, filteredCountries, flagUrl, toggleCountry, selectCountry,
      dialOpen, dialSearch, dialRef, dialSearchInput, selectedDial, filteredDials, toggleDial, selectDial,
      isLoading, completeRegistration
    };
  }
};
</script>

<style scoped>
.ob-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-color, #f5f5f5);
  padding: 24px 20px 40px;
}

/* Carte modal 2 panneaux */
.ob-modal {
  width: 100%;
  max-width: 1060px;
  min-height: 660px;
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  background: #fff;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 50px 110px -45px rgba(20, 48, 40, 0.5);
}

/* ---- Panneau gauche (image + logo) ---- */
.ob-left {
  position: relative;
  background: url('@/assets/peelochat_ecommerce.png') center / cover no-repeat;
  display: flex;
}
.ob-left-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(160deg, rgba(20,48,40,0.86) 0%, rgba(20,48,40,0.94) 100%);
}
.ob-left-content {
  position: relative;
  display: flex; flex-direction: column;
  padding: 40px 34px;
  color: #fff;
}
.ob-logo { width: 120px; height: auto; margin-bottom: auto; }
.ob-left-text h2 { font-size: 1.7rem; font-weight: 800; letter-spacing: -0.03em; margin: 0 0 10px; }
.ob-left-text p { font-size: 0.95rem; line-height: 1.55; color: rgba(255,255,255,0.78); margin: 0 0 26px; }
.ob-left-points { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 12px; }
.ob-left-points li { display: flex; align-items: center; gap: 10px; font-size: 0.9rem; color: rgba(255,255,255,0.9); }
.ob-left-points .dot { width: 8px; height: 8px; border-radius: 9999px; background: #DAFFD1; flex-shrink: 0; box-shadow: 0 0 0 4px rgba(218,255,209,0.18); }

/* ---- Panneau droit (formulaire) ---- */
.ob-right { padding: 40px 40px 36px; display: flex; flex-direction: column; }
.ob-right-head { margin-bottom: 22px; }
.ob-eyebrow {
  display: inline-block; background: #DAFFD1; color: #143028;
  font-size: 11px; font-weight: 900; letter-spacing: 0.14em; text-transform: uppercase;
  padding: 5px 12px; border-radius: 9999px; margin-bottom: 12px;
}
.ob-right-head h1 { font-size: 1.7rem; font-weight: 800; letter-spacing: -0.03em; color: #143028; margin: 0 0 6px; }
.ob-sub { font-size: 0.92rem; color: rgba(20,48,40,0.6); margin: 0; }

.ob-form { display: flex; flex-direction: column; gap: 16px; }
.ob-field { display: flex; flex-direction: column; position: relative; }
.ob-field label {
  font-size: 0.76rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
  color: rgba(20,48,40,0.55); margin-bottom: 7px;
}
.ob-field > input,
.ob-select select {
  width: 100%; box-sizing: border-box; padding: 12px 14px;
  border: 1px solid rgba(20,48,40,0.15); border-radius: 12px;
  font-size: 0.95rem; background: #fff; color: #143028; outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.ob-field > input:focus,
.ob-select select:focus { border-color: #143028; box-shadow: 0 0 0 3px rgba(20,48,40,0.08); }

.ob-select { position: relative; }
.ob-select select { appearance: none; -webkit-appearance: none; padding-right: 36px; cursor: pointer; }
.ob-select-arrow { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); pointer-events: none; color: rgba(20,48,40,0.5); }

/* Combobox pays */
.flag { width: 22px; height: 16px; border-radius: 3px; object-fit: cover; box-shadow: 0 0 0 1px rgba(20,48,40,0.08); flex-shrink: 0; }
.ob-combo { position: relative; }
.ob-combo-trigger {
  width: 100%; display: flex; align-items: center; gap: 10px;
  padding: 11px 14px; border: 1px solid rgba(20,48,40,0.15); border-radius: 12px;
  background: #fff; color: #143028; font-size: 0.95rem; cursor: pointer; text-align: left;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.ob-combo.open .ob-combo-trigger { border-color: #143028; box-shadow: 0 0 0 3px rgba(20,48,40,0.08); }
.ob-combo-value { flex: 1; }
.ob-combo-ph { flex: 1; color: rgba(20,48,40,0.45); }
.ob-combo-chevron { color: rgba(20,48,40,0.5); transition: transform 0.2s; }
.ob-combo.open .ob-combo-chevron { transform: rotate(180deg); }

.ob-combo-panel {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0; z-index: 20;
  background: #fff; border: 1px solid rgba(20,48,40,0.12); border-radius: 14px;
  box-shadow: 0 20px 45px -15px rgba(20,48,40,0.3); overflow: hidden;
}
.ob-combo-searchbox { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-bottom: 1px solid rgba(20,48,40,0.08); color: rgba(20,48,40,0.5); }
.ob-combo-searchbox input { flex: 1; border: none; outline: none; font-size: 0.9rem; color: #143028; background: transparent; }
.ob-combo-list { list-style: none; margin: 0; padding: 6px; max-height: 230px; overflow-y: auto; }
.ob-combo-list li { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: 9px; cursor: pointer; font-size: 0.9rem; color: #143028; }
.ob-combo-list li:hover { background: rgba(20,48,40,0.05); }
.ob-combo-list li.active { background: #DAFFD1; }
.ob-combo-name { flex: 1; }
.ob-combo-dial { color: rgba(20,48,40,0.5); font-weight: 600; font-size: 0.82rem; }
.ob-combo-empty { justify-content: center; color: rgba(20,48,40,0.5); cursor: default; }
.ob-combo-empty:hover { background: none; }

/* Téléphone */
.ob-phone {
  position: relative;
  display: flex; align-items: center;
  border: 1px solid rgba(20,48,40,0.15); border-radius: 12px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.ob-phone.open, .ob-phone:focus-within { border-color: #143028; box-shadow: 0 0 0 3px rgba(20,48,40,0.08); }
.ob-phone-cc {
  display: flex; align-items: center; gap: 7px;
  background: #FCFAF7; padding: 12px 12px; border: none; border-right: 1px solid rgba(20,48,40,0.12);
  border-radius: 11px 0 0 11px;
  font-weight: 700; color: #143028; white-space: nowrap; cursor: pointer;
  transition: background 0.15s;
}
.ob-phone-cc:hover { background: #efeee8; }
.ob-phone-cc .globe { font-size: 1rem; }
.ob-dial-chevron { color: rgba(20,48,40,0.5); }
.ob-phone input { flex: 1; border: none; padding: 12px 14px; min-width: 0; outline: none; font-size: 0.95rem; color: #143028; border-radius: 0 11px 11px 0; }
.ob-dial-panel { left: 0; right: 0; }

.input-error { border-color: #E5484D !important; }
.error-message { color: #C0392B; font-size: 0.8rem; margin-top: 0.25rem; }
.general-error-message { background: #FDE2E1; border: 1px solid #F2B8B5; color: #B42318; padding: 12px 14px; border-radius: 12px; font-size: 0.9rem; }

.ob-submit {
  background: #143028; color: #fff; padding: 14px; border: none; border-radius: 9999px;
  font-size: 0.95rem; font-weight: 800; cursor: pointer; transition: transform 0.2s, opacity 0.2s; margin-top: 6px;
}
.ob-submit:hover:not(:disabled) { transform: scale(1.02); }
.ob-submit:disabled { opacity: 0.55; cursor: not-allowed; }

@media (max-width: 820px) {
  .ob-modal { grid-template-columns: 1fr; max-width: 540px; min-height: 0; }
  .ob-left { min-height: 180px; }
  .ob-left-content { padding: 26px; }
  .ob-logo { width: 100px; margin-bottom: 18px; }
  .ob-left-points { display: none; }
  .ob-right { padding: 28px 24px 26px; }
}
</style>
